import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../App';
import { FileText, ArrowLeft, Eye, Calendar, Tag, List, Globe, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import useContentProtection from '../hooks/useContentProtection';
import PublicChatWidget from '../components/PublicChatWidget';

const PublicDocument = ({ customDomain }) => {
  const { t, i18n } = useTranslation();
  const { subdomain: paramSubdomain, documentId } = useParams();
  const [searchParams] = useSearchParams();
  const subdomain = customDomain || paramSubdomain;
  const accessKey = searchParams.get('key') || '';
  const [workspace, setWorkspace] = useState(null);
  const [docData, setDocData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeHeading, setActiveHeading] = useState('');

  // AI Chat state — uses shared <PublicChatWidget /> (same as helpdesk main page)
  const [chatOpen, setChatOpen] = useState(false);
  const [translating, setTranslating] = useState(false);

  useContentProtection(true);

  useEffect(() => {
    fetchData();
  }, [subdomain, documentId]);

  // Re-fetch with translation when language changes
  useEffect(() => {
    if (workspace && docData) {
      fetchTranslatedContent();
    }
  }, [i18n.language]);

  // Extract headings for Table of Contents
  const tableOfContents = useMemo(() => {
    if (!docData?.content) return [];
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(docData.content, 'text/html');
    const headings = doc.querySelectorAll('h1, h2, h3');
    
    return Array.from(headings).map((heading, index) => ({
      id: `heading-${index}`,
      text: heading.textContent,
      level: parseInt(heading.tagName.charAt(1)),
      originalTag: heading.tagName.toLowerCase()
    }));
  }, [docData?.content]);

  // Add IDs to headings in content for navigation
  const contentWithIds = useMemo(() => {
    if (!docData?.content) return '';
    
    let content = docData.content;
    let headingIndex = 0;
    
    content = content.replace(/<(h[1-3])([^>]*)>/gi, (match, tag, attrs) => {
      const id = `heading-${headingIndex}`;
      headingIndex++;
      return `<${tag}${attrs} id="${id}">`;
    });
    
    return content;
  }, [docData?.content]);

  // Scroll spy for active heading
  useEffect(() => {
    const handleScroll = () => {
      const headingElements = tableOfContents.map(h => window.document.getElementById(h.id)).filter(Boolean);
      
      for (let i = headingElements.length - 1; i >= 0; i--) {
        const element = headingElements[i];
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveHeading(tableOfContents[i].id);
            return;
          }
        }
      }
      
      if (tableOfContents.length > 0) {
        setActiveHeading(tableOfContents[0].id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tableOfContents]);

  const scrollToHeading = (id) => {
    const element = window.document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // AI Chat functions — chat now uses shared <PublicChatWidget /> (see JSX below).
  // The old inline chat handler was removed so the article page uses the same bot
  // as the Helpdesk landing page.

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const fetchData = async () => {
    try {
      const lang = i18n.language;
      const params = new URLSearchParams();
      if (lang !== 'en') params.append('lang', lang);
      if (accessKey) params.append('key', accessKey);
      const qs = params.toString();
      const keyParam = accessKey ? `?key=${accessKey}` : '';

      const [wsRes, docRes] = await Promise.all([
        axios.get(`${API_URL}/public/workspaces/${subdomain}${keyParam}`),
        axios.get(`${API_URL}/public/workspaces/${subdomain}/documents/${documentId}${qs ? '?' + qs : ''}`)
      ]);
      
      setWorkspace(wsRes.data);
      setDocData(docRes.data);
    } catch (error) {
      console.error('Error fetching document:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTranslatedContent = async () => {
    const lang = i18n.language;
    const params = new URLSearchParams();
    if (lang !== 'en') params.append('lang', lang);
    if (accessKey) params.append('key', accessKey);
    const qs = params.toString();

    setTranslating(true);
    try {
      const docRes = await axios.get(`${API_URL}/public/workspaces/${subdomain}/documents/${documentId}${qs ? '?' + qs : ''}`);
      setDocData(docRes.data);
    } catch (error) {
      console.error('Error fetching translated content:', error);
    } finally {
      setTranslating(false);
    }
  };

  // Generate back link based on whether it's custom domain or not
  const backLink = customDomain 
    ? (accessKey ? `/?key=${accessKey}` : '/') 
    : (accessKey ? `/portal/${subdomain}?key=${accessKey}` : `/portal/${subdomain}`);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-lg text-zinc-600">Loading...</div>
      </div>
    );
  }

  if (!docData || !workspace) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">Document Not Found</h2>
          <p className="text-zinc-600">The requested document does not exist or is not public.</p>
        </div>
      </div>
    );
  }

  const settings = workspace.settings || {};
  const primaryColor = settings.primary_color || '#7c3aed';
  const portalTheme = settings.portal_theme || 'light';
  const isDarkTheme = portalTheme === 'dark' || (portalTheme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  const bgColor = isDarkTheme ? 'bg-zinc-900' : 'bg-zinc-50';
  const cardBg = isDarkTheme ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200';
  const textColor = isDarkTheme ? 'text-zinc-100' : 'text-zinc-900';
  const textMuted = isDarkTheme ? 'text-zinc-400' : 'text-zinc-600';
  const headerBg = isDarkTheme ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200';

  return (
    <div className={`min-h-screen ${bgColor}`} data-testid="public-document">
      {/* Header */}
      <header className={`${headerBg} border-b sticky top-0 z-40`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link
              to={backLink}
              className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity"
              style={{ color: primaryColor }}
            >
              <ArrowLeft className="h-4 w-4" />
              {t('common.backTo', 'Back to')} {workspace.name}
            </Link>
            <div className="flex items-center gap-4">
              {/* Language Toggle */}
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4" style={{ color: textMuted }} />
                <select
                  value={i18n.language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className={`text-sm px-2 py-1 rounded border ${
                    isDarkTheme 
                      ? 'bg-zinc-800 border-zinc-600 text-zinc-200' 
                      : 'bg-white border-zinc-200 text-zinc-700'
                  }`}
                >
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                </select>
              </div>
              {settings.logo_url && (
                <img src={settings.logo_url} alt={workspace.name} className="h-8" />
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content with TOC Sidebar */}
      <main className="max-w-7xl mx-auto px-6 py-8 relative">
        {/* Table of Contents Sidebar - Fixed Position */}
        {tableOfContents.length > 0 && (
          <aside className="hidden lg:block fixed right-8 xl:right-[calc((100vw-1280px)/2+24px)] top-24 w-72 z-30">
            <div className={`${cardBg} border rounded-lg p-4 shadow-sm`} style={{ maxHeight: 'calc(100vh - 120px)' }}>
              <div className="flex items-center gap-2 mb-4">
                <List className="h-4 w-4" style={{ color: primaryColor }} />
                <h3 className={`text-sm font-semibold ${textColor}`}>
                  {t('document.tableOfContents', 'Table of Contents')}
                </h3>
              </div>
              <nav className="space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                {tableOfContents.map((heading) => (
                  <button
                    key={heading.id}
                    onClick={() => scrollToHeading(heading.id)}
                    className={`block w-full text-left text-sm py-1.5 px-2 rounded transition-colors ${
                      activeHeading === heading.id
                        ? isDarkTheme 
                          ? 'bg-zinc-700 text-white' 
                          : 'bg-violet-50 text-violet-700'
                        : isDarkTheme 
                          ? 'text-zinc-400 hover:bg-zinc-700' 
                          : 'text-zinc-600 hover:bg-zinc-100'
                    }`}
                    style={{
                      paddingLeft: `${(heading.level - 1) * 12 + 8}px`,
                      borderLeft: activeHeading === heading.id ? `2px solid ${primaryColor}` : '2px solid transparent'
                    }}
                  >
                    <span className="line-clamp-2">{heading.text}</span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>
        )}

        {/* Main Content - Adjusted width when TOC is present */}
        <div className={tableOfContents.length > 0 ? 'lg:pr-80' : ''}>
          <article className={`${cardBg} border rounded-lg overflow-hidden`}>
            {/* Document Header */}
            <div className="p-8 pb-6 border-b border-zinc-200">
              <h1 className={`text-4xl font-bold ${textColor} mb-6`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                {docData.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className={`flex items-center gap-2 ${textMuted}`}>
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(docData.created_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
                <div className={`flex items-center gap-2 ${textMuted}`}>
                  <Eye className="h-4 w-4" />
                  <span>{docData.views} {t('document.views', 'views')}</span>
                </div>
                {docData.category && (
                  <Link 
                    to={customDomain ? `/?category=${encodeURIComponent(docData.category)}` : `/portal/${subdomain}?category=${encodeURIComponent(docData.category)}`}
                    className="px-3 py-1 rounded-md text-sm font-medium hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
                  >
                    {docData.category}
                  </Link>
                )}
                {docData.tags && docData.tags.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {docData.tags.map((tag, idx) => (
                      <Link
                        key={idx}
                        to={customDomain ? `/?tag=${encodeURIComponent(tag)}` : `/portal/${subdomain}?tag=${encodeURIComponent(tag)}`}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                          isDarkTheme ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                        } transition-colors`}
                      >
                        <Tag className="h-3 w-3" />
                        {tag}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Document Content */}
            <div className="p-8">
              {translating && (
                <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-violet-50 border border-violet-200">
                  <div className="h-4 w-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-violet-700">{t('document.translating', 'Translating content...')}</span>
                </div>
              )}
              <div
                className={`document-content protected-content prose ${isDarkTheme ? 'prose-invert' : 'prose-zinc'} max-w-none prose-lg`}
                style={{ '--tw-prose-links': primaryColor }}
                dangerouslySetInnerHTML={{ __html: contentWithIds }}
              />
            </div>
          </article>
        </div>

        {/* Navigation */}
        <div className={`mt-8 flex justify-between items-center ${tableOfContents.length > 0 ? 'lg:pr-80' : ''}`}>
          <Link
            to={backLink}
            className={`inline-flex items-center gap-2 text-sm font-medium ${textMuted} hover:opacity-70 transition-opacity`}
          >
            <ArrowLeft className="h-4 w-4" />
            {t('document.browseAll', 'Browse all documents')}
          </Link>
        </div>
      </main>

      {/* AI Chat Widget — shared with the Helpdesk landing page for a consistent bot */}
      {settings.show_ai_chat !== false && (
        <>
          {/* Floating chat launcher */}
          {!chatOpen && (
            <button
              onClick={() => setChatOpen(true)}
              className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105"
              style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}
              data-testid="open-chat-button"
              aria-label="Open chat"
            >
              <Sparkles className="h-6 w-6 text-white" />
            </button>
          )}

          {chatOpen && (
            <PublicChatWidget
              subdomain={subdomain}
              workspaceName={workspace.name}
              primaryColor={primaryColor}
              onClose={() => setChatOpen(false)}
              settings={settings}
              accessKey={accessKey}
            />
          )}
        </>
      )}

      {/* Footer */}
      <footer className={`${headerBg} border-t mt-16`}>
        <div className={`max-w-7xl mx-auto px-6 py-6 text-center text-sm ${textMuted}`}>
          {settings.footer_text || (
            settings.contact_email ? (
              <>
                {t('common.needHelp', 'Need help? Contact us at')}{' '}
                <a href={`mailto:${settings.contact_email}`} className="font-medium" style={{ color: primaryColor }}>
                  {settings.contact_email}
                </a>
              </>
            ) : (
              <span>© {new Date().getFullYear()} {workspace.name}</span>
            )
          )}
        </div>
      </footer>
    </div>
  );
};

export default PublicDocument;
